import { z } from "zod";

import { DbEnum } from "../../../../generated/prisma-enums";
import { McpPinBreakageReasonSchema } from "../../../../shared/domain/mcp/mcp-pin-breakage";

/**
 * One agent skill whose pinned MCP tool will be refused at call time.
 *
 * ## The acceptance criterion this is
 *
 * *A removed tool shows as broken on every agent that had it attached.* The row is
 * keyed by the AGENT, because that is the thing an operator repairs — a removed
 * tool is one row per agent holding it, not one row on the server screen.
 *
 * It covers more than removal, and deliberately: the same call refuses a tool that
 * is pending, changed or rejected, and one whose approved contract has moved past
 * the hash this skill pinned. Listing only removals would leave three refusals with
 * no surface, and an operator would learn about them from a customer.
 *
 * ## Every field is one an operator acts on
 *
 * `agentId` and `label` name the skill to repair; `toolName` and `status` say what
 * is wrong with the tool; `breakageReason` says which of the two repairs it needs —
 * an administrator's approval, or re-attaching the agent — which the status alone
 * cannot distinguish, because a superseded pin sits on an APPROVED tool.
 */
export const McpBrokenToolPinSchema = z.object({
  /** `AgentToolConfig.id` — the skill whose call will be refused. */
  agentToolConfigId: z.string().uuid(),
  /** The agent holding that skill. The key the phrase "every agent" means. */
  agentId: z.string().uuid(),
  /** The skill's own label, so the row is actionable without a second lookup. */
  label: z.string(),
  /** `AgentExternalTool.id`, which IS `McpServer.externalToolId`. */
  serverId: z.string().uuid(),
  toolId: z.string().uuid(),
  /** The server's own name for the tool. Server-authored: render as text. */
  toolName: z.string(),
  status: z.enum(DbEnum.MCP_TOOL_STATUS_VALUES),
  breakageReason: McpPinBreakageReasonSchema
});

export type McpBrokenToolPin = z.infer<typeof McpBrokenToolPinSchema>;
